import { Controller } from "@hotwired/stimulus";
import { captureException, captureMessage } from "@sentry/browser";

import {
  BrowserExtension,
  ExtensionError,
  type ExtensionErrorCode,
} from "~/lib/browser-extension";
import logger from "~/lib/logger";

const log = logger();

export default class ExtensionImportController extends Controller {
  static override values = {
    extensionId: String,
    fromDate: String,
  } as const;

  static override targets = ["status", "button"];

  declare readonly fromDateValue: string;
  declare readonly statusTargets: HTMLElement[];
  declare readonly buttonTarget: HTMLButtonElement;

  private extension!: BrowserExtension;

  override connect(): void {
    this.extension = new BrowserExtension();
    this.checkInitialState();
  }

  private async checkInitialState(): Promise<void> {
    try {
      await this.extension.check();
    } catch (error) {
      if (error instanceof ExtensionError) {
        this.showStatusFromError(error);
      }
      this.setButtonState({ disabled: true });
    }
  }

  async importData(): Promise<void> {
    try {
      await this.extension.check();
    } catch (error) {
      this.showStatusFromError(error);
      return;
    }

    this.showStatus("importing");
    this.setButtonState({ disabled: true });

    try {
      await this.extension.usageTrackingImportHistory(this.fromDateValue);
      this.showStatus("success");
      window.location.reload();
    } catch (error) {
      log.error("Extension import error:", error);
      this.showStatusFromError(error);
      this.setButtonState({ disabled: false });
    }
  }

  private showStatusFromError(error: unknown): void {
    let code: ExtensionErrorCode | null = null;
    if (error instanceof ExtensionError) {
      code = error.code;
    }
    switch (code) {
      case "not-installed":
        captureMessage("Extension not installed", {
          level: "info",
        });
        this.showStatus("not-installed");
        break;
      case "communication-error":
      case "extension-rejected":
        captureException(error);
        this.showStatus("error");
        break;
      default:
        log.error("Unhandled extension error code:", error);
        captureException(error);
        this.showStatus("error");
    }
  }

  private setButtonState({ disabled }: { disabled: boolean }): void {
    this.buttonTarget.disabled = disabled;
  }

  private showStatus(
    status: "not-installed" | "importing" | "error" | "success",
  ): void {
    this.hideAllStatuses();

    const statusElement = this.statusTargets.find(
      (el) => el.dataset.status === status,
    );
    if (statusElement) {
      statusElement.classList.remove("hidden");
    }
  }

  private hideAllStatuses(): void {
    this.statusTargets.forEach((element) => {
      element.classList.add("hidden");
    });
  }
}
